import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * Keeps the keyboard focus in a section whose controls come and go. When a change removes or disables the control
 * that had the focus (a print job starts, stops or ends), the focus goes on to the first of `targets()` that can take
 * it, not to the page. A focus the user has put elsewhere is left alone. Returns {onFocus, onBlur} for the section's
 * root (`rootRef`).
 */
export function useFocusKeeper(rootRef, targets) {
  // The section's control that had the focus last.
  const lastRef = useRef(null);

  const onFocus = useCallback(
    (event) => {
      // A dialog of the section is rendered elsewhere in the page and gives the focus back itself.
      lastRef.current = rootRef.current?.contains(event.target) ? event.target : null;
    },
    [rootRef]
  );
  const onBlur = useCallback(
    (event) => {
      if (event.relatedTarget && !rootRef.current?.contains(event.relatedTarget)) lastRef.current = null;
    },
    [rootRef]
  );

  // After every change: a control removed takes the focus to the page at once; a control disabled keeps it until
  // the browser moves it there.
  useLayoutEffect(() => {
    const last = lastRef.current;
    if (!last || (last.isConnected && !last.disabled)) return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== last) return;
    lastRef.current = null;
    const target = targets().find((element) => element?.isConnected && !element.disabled);
    target?.focus({ preventScroll: true });
  });

  return { onFocus, onBlur };
}

/**
 * Puts the focus on `target()` once no dialog is left, if it is on the page by then. Headless UI gives the focus
 * back to the control that opened a dialog once the dialog has faded out, but not when that control has gone
 * meanwhile.
 */
export function settleFocus(target) {
  let frames = 0;
  const step = () => {
    if (document.querySelector('[role="dialog"]') && frames < 90) {
      frames += 1;
      requestAnimationFrame(step);
      return;
    }
    const active = document.activeElement;
    if (!active || active === document.body) target()?.focus();
  };
  requestAnimationFrame(step);
}
